<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Draggable from 'vuedraggable';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import ClosureAPI from 'dashboard/api/closure';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import NextInput from 'next/input/Input.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// SICARG MOD 3 v8 — Configuración de tipificaciones Sancor de 3 niveles.
// sub_reasons → sub_types; se eliminan payment_statuses y show_commitment_date.
const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const isLoading = ref(false);
const settings = ref({
  require_on_resolve: false,
  show_managed_amount: true,
  show_notes: true,
  reasons: [],
});

const slugify = label =>
  (label || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

// Key local (drag & drop) — `value` puede estar vacío/duplicado mientras se edita
// una tipificación nueva, así que no sirve como item-key de Draggable.
let dragKeySeq = 0;
const genDragKey = () => `k${Date.now()}_${dragKeySeq++}`;

const load = async () => {
  try {
    const { data } = await ClosureAPI.getSettings();
    settings.value = {
      require_on_resolve:  data.require_on_resolve,
      show_managed_amount: data.show_managed_amount,
      show_notes:          data.show_notes,
      // Normalizar sub_types (compat. con datos viejos que tenían sub_reasons)
      reasons: (data.reasons || []).map(r => ({
        _key:                genDragKey(),
        value:               r.value || '',
        label:               r.label || '',
        active:              r.active !== false,
        visible_for_agents:  r.visible_for_agents !== false,
        requires_managed_amount: r.requires_managed_amount === true,
        requires_full_record: r.requires_full_record === true,
        sub_types: (r.sub_types || r.sub_reasons || []).map(st => ({
          _key:          genDragKey(),
          value:         st.value || '',
          label:         st.label || '',
          requires_form: st.requires_form || null,
          agreement_type: st.agreement_type || null,
          form_fields:   st.form_fields || [],
        })),
      })),
    };
  } catch {
    // Sin configuración previa
  }
};

onMounted(load);

const addReason = () => {
  settings.value.reasons.push({
    _key: genDragKey(), value: '', label: '', active: true, visible_for_agents: true,
    requires_managed_amount: false, requires_full_record: false, sub_types: [],
  });
};
const toggleReasonActive = reason => {
  reason.active = !reason.active;
};
const toggleReasonVisibility = reason => {
  reason.visible_for_agents = !reason.visible_for_agents;
};
const toggleReasonRequiresAmount = reason => {
  reason.requires_managed_amount = !reason.requires_managed_amount;
};
const toggleReasonRequiresFullRecord = reason => {
  reason.requires_full_record = !reason.requires_full_record;
};
const removeReason = reason => {
  const index = settings.value.reasons.indexOf(reason);
  if (index !== -1) settings.value.reasons.splice(index, 1);
};
const addSubType = reason => {
  if (!reason.sub_types) reason.sub_types = [];
  reason.sub_types.push({
    _key: genDragKey(), value: '', label: '', requires_form: null, agreement_type: null, form_fields: [],
  });
};
const removeSubType = (reason, subType) => {
  const subIndex = reason.sub_types.indexOf(subType);
  if (subIndex !== -1) reason.sub_types.splice(subIndex, 1);
};

// Normaliza preservando metadatos del sistema (requires_form, agreement_type, form_fields)
// para que la migración Sancor no se pierda al editar desde la UI.
const normalizeSubTypes = list =>
  (list || [])
    .filter(st => st.label && st.label.trim())
    .map(st => ({
      value:         st.value && st.value.trim() ? st.value : slugify(st.label),
      label:         st.label.trim(),
      requires_form: st.requires_form || null,
      agreement_type: st.agreement_type || null,
      form_fields:   st.form_fields || [],
    }));

const normalizeReasons = list =>
  list
    .filter(item => item.label && item.label.trim())
    .map(item => ({
      value:               item.value && item.value.trim() ? item.value : slugify(item.label),
      label:               item.label.trim(),
      active:              item.active !== false,
      visible_for_agents:  item.visible_for_agents !== false,
      requires_managed_amount: item.requires_managed_amount === true,
      requires_full_record: item.requires_full_record === true,
      sub_types:           normalizeSubTypes(item.sub_types || []),
    }));

const save = async () => {
  isLoading.value = true;
  try {
    await ClosureAPI.updateSettings({
      require_on_resolve:  settings.value.require_on_resolve,
      show_managed_amount: settings.value.show_managed_amount,
      show_notes:          settings.value.show_notes,
      reasons:             normalizeReasons(settings.value.reasons),
    });
    await load();
    useAlert(t('CLOSURE_SETTINGS.API.SUCCESS'));
  } catch {
    useAlert(t('CLOSURE_SETTINGS.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const goToStatistics = () => router.push(accountScopedRoute('closure_statistics_index'));
const goToPanel      = () => router.push(accountScopedRoute('closure_panel_index'));
</script>

<template>
  <SettingsLayout :no-records-found="false" class="gap-6">
    <template #header>
      <BaseSettingsHeader
        :title="t('CLOSURE_SETTINGS.HEADER.TITLE')"
        :description="t('CLOSURE_SETTINGS.HEADER.DESCRIPTION')"
      >
        <template #actions>
          <NextButton faded slate sm icon="i-lucide-layout-dashboard" :label="t('CLOSURE_SETTINGS.PANEL_LINK')" @click="goToPanel" />
          <NextButton faded slate sm icon="i-lucide-bar-chart-3"     :label="t('CLOSURE_SETTINGS.STATISTICS_LINK')" @click="goToStatistics" />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-6 mt-4 max-w-2xl">

        <!-- Obligatoriedad -->
        <div class="flex justify-between items-center p-4 rounded-lg border border-n-weak">
          <div>
            <p class="text-sm font-medium text-n-slate-12">{{ t('CLOSURE_SETTINGS.REQUIRED.TITLE') }}</p>
            <p class="text-sm text-n-slate-11">{{ t('CLOSURE_SETTINGS.REQUIRED.NOTE') }}</p>
          </div>
          <Switch v-model="settings.require_on_resolve" />
        </div>

        <!-- Campos visibles en el formulario de cierre -->
        <div class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak">
          <p class="text-sm font-medium text-n-slate-12">{{ t('CLOSURE_SETTINGS.FIELDS.TITLE') }}</p>
          <label class="flex gap-2 justify-between items-center">
            <span class="text-sm text-n-slate-11">{{ t('CLOSURE_SETTINGS.FIELDS.MANAGED_AMOUNT') }}</span>
            <Switch v-model="settings.show_managed_amount" />
          </label>
          <label class="flex gap-2 justify-between items-center">
            <span class="text-sm text-n-slate-11">{{ t('CLOSURE_SETTINGS.FIELDS.NOTES') }}</span>
            <Switch v-model="settings.show_notes" />
          </label>
        </div>

        <!-- Árbol de tipificaciones (3 niveles) -->
        <div class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak">
          <div class="flex justify-between items-center">
            <div>
              <p class="text-sm font-medium text-n-slate-12">{{ t('CLOSURE_SETTINGS.REASONS.TITLE') }}</p>
              <p class="text-xs text-n-slate-10 mt-0.5">{{ t('CLOSURE_SETTINGS.REASONS.SUBTITLE') }}</p>
            </div>
            <NextButton faded slate xs icon="i-lucide-plus" :label="t('CLOSURE_SETTINGS.ADD')" @click="addReason" />
          </div>

          <Draggable
            v-model="settings.reasons"
            item-key="_key"
            handle=".reason-drag-handle"
            tag="div"
            class="flex flex-col gap-2"
          >
            <template #item="{ element: reason }">
              <div
                class="flex flex-col gap-2 p-3 rounded-md border border-n-weak/60 bg-n-alpha-1 transition-opacity"
                :class="{ 'opacity-50': !reason.active }"
              >
                <!-- Categoría -->
                <div class="flex gap-2 items-center">
                  <Icon
                    icon="i-woot-drag-indicator"
                    class="reason-drag-handle size-4 text-n-slate-11 shrink-0 cursor-move"
                  />
                  <NextInput
                    v-model="reason.label"
                    class="flex-1"
                    :placeholder="t('CLOSURE_SETTINGS.REASONS.PLACEHOLDER')"
                    :disabled="!reason.active"
                  />
                  <NextButton
                    ghost slate xs
                    :icon="reason.active ? 'i-lucide-eye' : 'i-lucide-eye-off'"
                    :title="reason.active ? 'Desactivar tipificación' : 'Activar tipificación'"
                    @click="toggleReasonActive(reason)"
                  />
                  <NextButton
                    ghost slate xs
                    :icon="reason.visible_for_agents ? 'i-lucide-users' : 'i-lucide-bot'"
                    :title="reason.visible_for_agents ? t('CLOSURE_SETTINGS.REASONS.VISIBLE_TOOLTIP') : t('CLOSURE_SETTINGS.REASONS.HIDDEN_TOOLTIP')"
                    @click="toggleReasonVisibility(reason)"
                  />
                  <NextButton
                    ghost xs
                    :color="reason.requires_managed_amount ? 'teal' : 'slate'"
                    :icon="reason.requires_managed_amount ? 'i-lucide-dollar-sign' : 'i-lucide-ban'"
                    :title="reason.requires_managed_amount ? t('CLOSURE_SETTINGS.REASONS.REQUIRES_AMOUNT_TOOLTIP') : t('CLOSURE_SETTINGS.REASONS.NOT_REQUIRES_AMOUNT_TOOLTIP')"
                    @click="toggleReasonRequiresAmount(reason)"
                  />
                  <NextButton
                    ghost xs
                    :color="reason.requires_full_record ? 'teal' : 'slate'"
                    :icon="reason.requires_full_record ? 'i-lucide-list-checks' : 'i-lucide-list'"
                    :title="reason.requires_full_record ? t('CLOSURE_SETTINGS.REASONS.REQUIRES_FULL_RECORD_TOOLTIP') : t('CLOSURE_SETTINGS.REASONS.NOT_REQUIRES_FULL_RECORD_TOOLTIP')"
                    @click="toggleReasonRequiresFullRecord(reason)"
                  />
                  <NextButton ghost ruby xs icon="i-lucide-trash-2" title="Eliminar tipificación" @click="removeReason(reason)" />
                </div>

                <!-- Sub-tipos ligados a esta categoría -->
                <div class="flex flex-col gap-2 pl-4 ml-1 border-l border-n-weak">
                  <Draggable
                    v-model="reason.sub_types"
                    item-key="_key"
                    handle=".subtype-drag-handle"
                    tag="div"
                    class="flex flex-col gap-2"
                  >
                    <template #item="{ element: sub }">
                      <div class="flex gap-2 items-center">
                        <Icon
                          icon="i-woot-drag-indicator"
                          class="subtype-drag-handle size-4 text-n-slate-11 shrink-0 cursor-move"
                        />
                        <NextInput
                          v-model="sub.label"
                          class="flex-1"
                          :placeholder="t('CLOSURE_SETTINGS.SUB_TYPES.PLACEHOLDER')"
                        />
                        <!-- Badge si tiene formulario de pago (no editable, solo informativo) -->
                        <span
                          v-if="sub.requires_form"
                          class="text-xs px-2 py-0.5 rounded-full bg-n-teal-3 text-n-teal-11 shrink-0"
                          :title="t('CLOSURE_SETTINGS.SUB_TYPES.REQUIRES_FORM_HINT')"
                        >
                          {{ t('CLOSURE_SETTINGS.SUB_TYPES.REQUIRES_FORM') }}
                        </span>
                        <NextButton ghost ruby xs icon="i-lucide-trash-2" @click="removeSubType(reason, sub)" />
                      </div>
                    </template>
                  </Draggable>
                  <NextButton
                    ghost slate xs icon="i-lucide-plus"
                    :label="t('CLOSURE_SETTINGS.SUB_TYPES.ADD')"
                    class="self-start"
                    @click="addSubType(reason)"
                  />
                </div>
              </div>
            </template>
          </Draggable>

          <p v-if="!settings.reasons.length" class="text-sm text-n-slate-10">
            {{ t('CLOSURE_SETTINGS.REASONS.EMPTY') }}
          </p>
        </div>

        <div>
          <NextButton blue :is-loading="isLoading" @click="save">
            {{ t('CLOSURE_SETTINGS.SAVE') }}
          </NextButton>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
